import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const CartPage = () => {
  const { items, updateQuantity, removeItem } = useCart();
  const totalItems = useCart().getTotalItems();
  const totalPrice = useCart().getTotalPrice();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shippingCost, setShippingCost] = useState(99);

  const formatMXN = (value: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(
      doc(db, 'settings', 'deliveryZones'),
      (snap) => {
        const data = snap.data() as any;
        const cost = typeof data?.shippingCost === 'number' && data.shippingCost >= 0 ? data.shippingCost : 99;
        setShippingCost(cost);
      },
      (err) => {
        console.warn('[Cart] Error obteniendo costo de envío', err);
      }
    );
    return () => unsub();
  }, []);

  const handleQuantityChange = (key: string, change: number) => {
    const item = items.find(item => item.key === key);
    if (item) {
      const newQuantity = item.quantity + change;
      if (newQuantity > 0) {
        updateQuantity(key, newQuantity);
      }
    }
  };

  const handleRemoveItem = (key: string) => {
    removeItem(key);
  };

  const handleCheckout = () => {
    if (!user) {
      navigate('/profile');
      return;
    }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#333' }}>
          Carrito de Compras
        </Typography>
        <Box sx={{ py: 8 }}>
          <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
            Tu carrito está vacío
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/catalog')}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' }
            }}
          >
            VER CATÁLOGO
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#333', fontFamily: '"Playfair Display", serif', fontWeight: 700, mb: 4 }}>
        Carrito de Compras ({totalItems} items)
      </Typography>

      {!user && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Por favor, inicia sesión para completar tu compra.
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {items.map((item) => (
            <Card key={item.key} sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <CardMedia
                      component="img"
                      height="120"
                      image={item.product.image}
                      alt={item.product.name}
                      sx={{ objectFit: 'cover', borderRadius: 2, aspectRatio: '1/1' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" component="h3" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
                      {item.product.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      {item.product.description}
                    </Typography>
                    {item.product.variantLabel && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontStyle: 'italic' }}>
                        {item.product.variantLabel}
                      </Typography>
                    )}
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      {formatMXN(item.product.price)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: { xs: 'flex-start', sm: 'center' } }}>
                      <IconButton
                        onClick={() => handleQuantityChange(item.key, -1)}
                        disabled={item.quantity <= 1}
                        size="small"
                        sx={{ border: '1px solid #eee' }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ mx: 2, minWidth: '30px', textAlign: 'center', fontWeight: 600 }}>
                        {item.quantity}
                      </Typography>
                      <IconButton
                        onClick={() => handleQuantityChange(item.key, 1)}
                        size="small"
                        sx={{ border: '1px solid #eee' }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: { xs: 'left', sm: 'center' } }}>
                      Subtotal: <Box component="span" sx={{ fontWeight: 'bold', color: '#333' }}>{formatMXN(item.product.price * item.quantity)}</Box>
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleRemoveItem(item.key)}
                      fullWidth
                      sx={{ borderRadius: '50px', textTransform: 'none' }}
                    >
                      Eliminar
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 24, boxShadow: '0 8px 30px rgba(0,0,0,0.1)', borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#333', fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                Resumen del Pedido
              </Typography>
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="text.secondary">Subtotal:</Typography>
                  <Typography fontWeight={500}>{formatMXN(totalPrice)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="text.secondary">Envío:</Typography>
                  <Typography fontWeight={500}>{formatMXN(shippingCost)}</Typography>
                </Box>
                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    {formatMXN(totalPrice + shippingCost)}
                  </Typography>
                </Box>
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleCheckout}
                disabled={items.length === 0}
                size="large"
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  py: 1.8,
                  borderRadius: '50px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
                }}
              >
                {user ? 'Proceder al Pago' : 'Iniciar Sesión para Comprar'}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/catalog')}
                sx={{ mt: 2, py: 1.5, borderRadius: '50px', textTransform: 'none', fontWeight: 600 }}
              >
                Seguir Comprando
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CartPage;